import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import {
  Check as FiCheck,
  X as FiX,
  Eye as FiEye,
  Receipt as FiReceipt,
  PencilSquare as FiEdit,
  Tools as FiTools,
  Trash3 as FiTrash2,
  ThreeDotsVertical as FiMoreVertical,
} from 'react-bootstrap-icons';
import type { Order } from '../../api/orders';
import styles from './Orders.module.scss';

const orderActionsPopperConfig = {
  strategy: 'fixed' as const,
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    {
      name: 'preventOverflow',
      options: {
        boundary: 'viewport',
        altBoundary: true,
        padding: 8,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: ['top-end', 'bottom-end', 'top-start', 'bottom-start'],
      },
    },
  ],
};

export interface OrderRowActionsProps {
  order: Order;
  orderApprovalRequired: boolean;
  layout?: 'table' | 'mobile';
  canProfitSyncRepair: boolean;
  canRetryProfitInventorySync: boolean;
  /** When false, hide mutate actions (approve/edit/delete/invoice). */
  canWrite?: boolean;
  onView: (order: Order) => void;
  onApprove: (order: Order) => void;
  onReject: (order: Order) => void;
  onMarkAsInvoiced: (order: Order) => void;
  onEdit: (order: Order) => void;
  onProfitSyncRepair: (order: Order) => void;
  onDelete: (order: Order) => void;
}

function getInvoicedActionLabel(
  order: Order,
  canRetryProfitInventorySync: boolean
): string {
  if (!order.invoiced) return 'Facturar';
  return canRetryProfitInventorySync ? 'Actualizar inventario' : 'Facturado';
}

function getInvoicedActionTitle(
  order: Order,
  canRetryProfitInventorySync: boolean
): string {
  if (!order.invoiced) return 'Marcar como facturada';
  return canRetryProfitInventorySync
    ? 'Volver a encolar inventario desde Profit Plus'
    : 'La venta ya está facturada';
}

const OrderRowActions: React.FC<OrderRowActionsProps> = ({
  order,
  orderApprovalRequired,
  layout = 'table',
  canProfitSyncRepair,
  canRetryProfitInventorySync,
  canWrite = true,
  onView,
  onApprove,
  onReject,
  onMarkAsInvoiced,
  onEdit,
  onProfitSyncRepair,
  onDelete,
}) => {
  const isTable = layout === 'table';
  const showApprovalActions =
    canWrite &&
    orderApprovalRequired &&
    order.approvalState === 'pending';
  const showEditAction = canWrite && order.approvalState === 'pending';
  const showProfitRepair = canWrite && canProfitSyncRepair;
  const invoicedDisabled =
    !canWrite || Boolean(order.invoiced && !canRetryProfitInventorySync);
  const hasMenuItems = showEditAction || showProfitRepair;

  const primaryBtnClass = isTable ? styles.actionBtn : 'flex-fill';
  const labeled = !isTable;

  return (
    <div
      className={isTable ? styles.actionsGroup : 'd-flex flex-wrap gap-1 w-100'}
      onClick={e => e.stopPropagation()}
    >
      {showApprovalActions && (
        <>
          <Button
            variant="outline-success"
            size="sm"
            onClick={() => onApprove(order)}
            title="Aprobar venta"
            className={primaryBtnClass}
          >
            <FiCheck className={labeled ? 'me-1' : undefined} />
            {labeled ? 'Aprobar' : null}
          </Button>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={() => onReject(order)}
            title="Rechazar venta"
            className={primaryBtnClass}
          >
            <FiX className={labeled ? 'me-1' : undefined} />
            {labeled ? 'Rechazar' : null}
          </Button>
        </>
      )}
      <Button
        variant="outline-secondary"
        size="sm"
        onClick={() => onMarkAsInvoiced(order)}
        disabled={invoicedDisabled}
        title={
          canWrite
            ? getInvoicedActionTitle(order, canRetryProfitInventorySync)
            : 'Solo lectura'
        }
        className={primaryBtnClass}
      >
        <FiReceipt className={labeled ? 'me-1' : undefined} />
        {labeled
          ? getInvoicedActionLabel(order, canRetryProfitInventorySync)
          : null}
      </Button>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => onView(order)}
        title="Ver detalles"
        className={primaryBtnClass}
      >
        <FiEye className={labeled ? 'me-1' : undefined} />
        {labeled ? 'Ver' : null}
      </Button>

      {canWrite ? (
      <Dropdown
        align="end"
        className={styles.actionsDropdown}
        onClick={e => e.stopPropagation()}
      >
        <Dropdown.Toggle
          variant="outline-secondary"
          size="sm"
          id={`order-actions-${order.id}`}
          className={`${styles.actionBtn} ${styles.moreActionsToggle}`}
          title="Más acciones"
        >
          <FiMoreVertical />
        </Dropdown.Toggle>
        <Dropdown.Menu popperConfig={orderActionsPopperConfig} renderOnMount>
          {showEditAction && (
            <Dropdown.Item onClick={() => onEdit(order)}>
              <FiEdit className="me-2" />
              Editar venta
            </Dropdown.Item>
          )}
          {showProfitRepair && (
            <Dropdown.Item onClick={() => onProfitSyncRepair(order)}>
              <FiTools className="me-2" />
              Completar datos Profit Plus
            </Dropdown.Item>
          )}
          {hasMenuItems ? <Dropdown.Divider /> : null}
          <Dropdown.Item
            className="text-danger"
            onClick={() => onDelete(order)}
          >
            <FiTrash2 className="me-2" />
            Mover a papelera
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
      ) : null}
    </div>
  );
};

export default OrderRowActions;
